import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { aml, type AmlAlert } from "@/api/aml";
import { ApiError } from "@/api/http";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/Toast";
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  FormError,
  Input,
  Label,
  Select,
} from "@/components/ui";

/**
 * AML alert queue. Compliance officer triages rule-engine hits raised
 * on every deposit / withdrawal. `cleared` ends the case as a false
 * positive; `escalated` flags it for SAR filing (10A.6); `reviewing`
 * is a hold while the officer gathers more info.
 */
export function AdminAmlAlertsPage() {
  const [status, setStatus] = useState<AmlAlert["status"] | "">("open");
  const [severity, setSeverity] = useState<AmlAlert["severity"] | "">("");
  const [decideTarget, setDecideTarget] = useState<AmlAlert | null>(null);

  const list = useQuery({
    queryKey: ["admin-aml", status, severity],
    queryFn: () =>
      aml.list({
        status: status || undefined,
        severity: severity || undefined,
      }),
    refetchInterval: 30_000,
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-base font-semibold text-term-fg">AML alerts</h1>
          <p className="text-2xs text-term-mute">
            Transaction-monitoring rule hits on deposits + withdrawals.
            Block-eligible rules already refused the transaction; lower-severity
            alerts queue here for compliance review.
          </p>
        </div>
        <div className="flex gap-2">
          <Select
            className="w-40"
            value={status}
            onChange={(e) => setStatus(e.target.value as typeof status)}
          >
            <option value="open">Open</option>
            <option value="reviewing">Reviewing</option>
            <option value="cleared">Cleared</option>
            <option value="escalated">Escalated</option>
            <option value="">All</option>
          </Select>
          <Select
            className="w-40"
            value={severity}
            onChange={(e) => setSeverity(e.target.value as typeof severity)}
          >
            <option value="">All severities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </Select>
        </div>
      </div>

      <Card>
        <CardBody className="p-0">
          {list.isLoading ? (
            <p className="p-3 text-sm text-term-mute">Loading…</p>
          ) : !list.data || list.data.length === 0 ? (
            <EmptyState
              title="No alerts"
              description="Nothing in this queue. Lower the filter, or wait — alerts arrive as the rule engine flags transactions."
            />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">When</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">User</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Rule</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Severity</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Status</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Trigger</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Detail</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase">Action</th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((a) => (
                  <tr key={a.id} className="border-t border-term-border hover:bg-term-elev align-top">
                    <td className="px-3 py-1.5 text-term-dim">
                      {new Date(a.created_at).toLocaleString()}
                    </td>
                    <td className="px-3 py-1.5">
                      <div className="text-term-fg">{a.user_email ?? "—"}</div>
                      {a.user_name && (
                        <div className="text-2xs text-term-dim">{a.user_name}</div>
                      )}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-term-fg">{a.rule_id}</td>
                    <td className="px-3 py-1.5">{severityBadge(a.severity)}</td>
                    <td className="px-3 py-1.5">{statusBadge(a.status)}</td>
                    <td className="px-3 py-1.5 text-term-dim">{a.trigger_type}</td>
                    <td className="px-3 py-1.5 max-w-md">
                      <pre className="whitespace-pre-wrap break-words font-mono text-2xs text-term-dim">
                        {Object.keys(a.payload).length
                          ? JSON.stringify(a.payload, null, 0)
                          : "—"}
                      </pre>
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      {(a.status === "open" || a.status === "reviewing") && (
                        <Button onClick={() => setDecideTarget(a)}>Decide</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      {decideTarget && (
        <DecideModal
          alert={decideTarget}
          onClose={() => setDecideTarget(null)}
        />
      )}
    </div>
  );
}

function severityBadge(s: AmlAlert["severity"]) {
  const tone =
    s === "critical" || s === "high"
      ? "bad"
      : s === "medium"
      ? "warn"
      : "neutral";
  return <Badge tone={tone as "bad" | "warn" | "neutral"}>{s}</Badge>;
}

function statusBadge(s: AmlAlert["status"]) {
  const tone =
    s === "cleared"
      ? "good"
      : s === "escalated"
      ? "bad"
      : s === "reviewing"
      ? "warn"
      : "neutral";
  return <Badge tone={tone as "good" | "bad" | "warn" | "neutral"}>{s}</Badge>;
}

function DecideModal({
  alert,
  onClose,
}: {
  alert: AmlAlert;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [status, setStatus] = useState<AmlAlert["status"]>("cleared");
  const [note, setNote] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const decide = useMutation({
    mutationFn: () =>
      aml.decide(alert.id, { status, note: note || undefined }),
    onSuccess: (r) => {
      toast.push(
        `Alert ${r.rule_id}: ${r.status}`,
        r.status === "cleared" ? "success" : r.status === "escalated" ? "error" : "warn",
      );
      qc.invalidateQueries({ queryKey: ["admin-aml"] });
      onClose();
    },
    onError: (e) =>
      setErr(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e)),
  });

  return (
    <Modal open onClose={onClose} title={`Decide · ${alert.rule_id}`}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          decide.mutate();
        }}
        className="space-y-3"
      >
        <p className="text-2xs text-term-dim">
          Rule <span className="font-mono">{alert.rule_id}</span> fired at{" "}
          {alert.severity} severity on {alert.trigger_type} for{" "}
          <span className="font-mono">{alert.user_email}</span>.
        </p>
        <pre className="overflow-auto rounded bg-term-elev p-2 font-mono text-2xs text-term-dim">
          {JSON.stringify(alert.payload, null, 2)}
        </pre>
        <div>
          <Label>Decision</Label>
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value as AmlAlert["status"])}
          >
            <option value="cleared">cleared — false positive</option>
            <option value="reviewing">reviewing — hold while gathering info</option>
            <option value="escalated">escalated — refer for SAR</option>
          </Select>
        </div>
        <div>
          <Label>Note (audit-logged)</Label>
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. customer's salary deposit, verified payslip on file"
          />
        </div>
        <FormError>{err}</FormError>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={decide.isPending}>
            Save decision
          </Button>
        </div>
      </form>
    </Modal>
  );
}
